import {
    POPUP_APPLIED_ACTION_OPTIONS,
    POPUP_COMPLIANCE_RESTRICTION_PERIOD_OPTIONS,
    POPUP_DEPOSIT_RESTRICTION_PERIOD_OPTIONS,
    POPUP_RISK_LEVEL_OPTIONS,
    PopupAppliedActionFormValueType,
    PopupAppliedActionRestrictionPeriodFormValueType,
    PopupRiskLevelType,
    showsAppliedActionRestrictionPeriod,
} from './MarkersOfHarmPopupTab.types';

export type PopupConfigurationSummarySegmentType = string | { text: string; emphasized: true };

export type PopupConfigurationSummaryItemType = {
    id: string;
    segments: PopupConfigurationSummarySegmentType[];
};

const findOptionLabel = <T extends string>(
    options: Array<{ label: string; value: T }>,
    value: T | ''
): string | null => (value === '' ? null : (options.find((option) => option.value === value)?.label ?? null));

const getAppliedActionRestrictionPeriodLabel = (
    appliedAction: PopupAppliedActionFormValueType,
    restrictionPeriod: PopupAppliedActionRestrictionPeriodFormValueType
): string | null => {
    if (!showsAppliedActionRestrictionPeriod(appliedAction) || restrictionPeriod === '') {
        return null;
    }

    if (appliedAction === 'compliance-restriction') {
        return findOptionLabel(POPUP_COMPLIANCE_RESTRICTION_PERIOD_OPTIONS, restrictionPeriod);
    }

    if (appliedAction === 'deposit-restriction') {
        return findOptionLabel(POPUP_DEPOSIT_RESTRICTION_PERIOD_OPTIONS, restrictionPeriod);
    }

    return null;
};

const formatIsoDurationForSummary = (value: string): string => {
    const rollingMap: Record<string, string> = {
        P1D: '1 day',
        P1W: '1 week',
        P1M: '1 month',
    };

    if (rollingMap[value] !== undefined) {
        return rollingMap[value];
    }

    const hoursMatch = /^PT(\d+)H$/.exec(value);
    if (hoursMatch !== null) {
        const hoursValue = hoursMatch[1];
        if (hoursValue === undefined) {
            return value;
        }
        const hours = parseInt(hoursValue, 10);
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }

    const minutesMatch = /^PT(\d+)M$/.exec(value);
    if (minutesMatch !== null) {
        const minutesValue = minutesMatch[1];
        if (minutesValue === undefined) {
            return value;
        }
        const minutes = parseInt(minutesValue, 10);
        return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }

    return value;
};

const buildDisplayConditionItem = ({
    triggerTemplateLabel,
    triggerParamValues,
}: {
    triggerTemplateLabel: string | null;
    triggerParamValues: Array<{ label: string; value: string }>;
}): PopupConfigurationSummaryItemType | null => {
    if (triggerTemplateLabel === null) {
        return null;
    }

    if (triggerParamValues.length === 0) {
        return {
            id: 'display-condition',
            segments: ['Pop-Up uses trigger template ', { text: triggerTemplateLabel, emphasized: true }, '.'],
        };
    }

    const paramsPhrase = triggerParamValues
        .map((item) => `${item.label}: ${formatIsoDurationForSummary(item.value)}`)
        .join(', ');

    return {
        id: 'display-condition',
        segments: [
            'Pop-Up uses trigger ',
            { text: triggerTemplateLabel, emphasized: true },
            ' with params ',
            { text: paramsPhrase, emphasized: true },
            '.',
        ],
    };
};

export const buildPopupConfigurationSummaryItems = ({
    triggerTemplateLabel,
    triggerParamValues,
    appliedAction,
    appliedActionRestrictionPeriod,
    riskLevel,
    hasUnsavedChanges,
}: {
    triggerTemplateLabel: string | null;
    triggerParamValues: Array<{ label: string; value: string }>;
    appliedAction: PopupAppliedActionFormValueType;
    appliedActionRestrictionPeriod: PopupAppliedActionRestrictionPeriodFormValueType;
    riskLevel: PopupRiskLevelType;
    hasUnsavedChanges: boolean;
}): PopupConfigurationSummaryItemType[] => {
    const items: PopupConfigurationSummaryItemType[] = [];

    const displayConditionItem = buildDisplayConditionItem({
        triggerTemplateLabel,
        triggerParamValues,
    });

    if (displayConditionItem === null) {
        items.push({
            id: 'display-condition-empty',
            segments: ['Select trigger settings below to preview how this Pop-Up will behave.'],
        });
    } else {
        items.push(displayConditionItem);
    }

    const appliedActionLabel = findOptionLabel(POPUP_APPLIED_ACTION_OPTIONS, appliedAction);
    const restrictionPeriodLabel = getAppliedActionRestrictionPeriodLabel(
        appliedAction,
        appliedActionRestrictionPeriod
    );

    if (appliedActionLabel !== null) {
        if (appliedAction === 'none') {
            items.push({
                id: 'applied-action',
                segments: ['No automatic action will be applied to the user account.'],
            });
        } else if (restrictionPeriodLabel === null) {
            items.push({
                id: 'applied-action',
                segments: [
                    'Once displayed, ',
                    { text: appliedActionLabel, emphasized: true },
                    ' will be applied automatically to the user account.',
                ],
            });
        } else {
            items.push({
                id: 'applied-action',
                segments: [
                    'Once displayed, ',
                    { text: appliedActionLabel, emphasized: true },
                    ' will be applied automatically for ',
                    { text: restrictionPeriodLabel.toLowerCase(), emphasized: true },
                    '.',
                ],
            });
        }
    }

    const riskLevelLabel = findOptionLabel(POPUP_RISK_LEVEL_OPTIONS, riskLevel);
    if (riskLevelLabel !== null) {
        items.push({
            id: 'risk-level',
            segments: ['Internal risk level: ', { text: riskLevelLabel, emphasized: true }, '.'],
        });
    }

    if (hasUnsavedChanges) {
        items.push({
            id: 'save-note',
            segments: ['Save new configuration to apply changes.'],
        });
    }

    return items;
};
